import type { Task } from '@dyspatch/core';
import { MessageSquarePlus } from 'lucide-react';
import { useMemo } from 'react';
import {
  DependencyChips,
  Empty,
  PrMark,
  RepoChip,
  SizeChip,
  TaskKey,
} from '@/components/common/bits';
import { badgeVariantForTone, TaskIcon, taskTone } from '@/components/common/StatusIcon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RetryHeldButton } from '@/features/actions/GateActions';
import { PausedChip } from '@/features/actions/PauseActions';
import { dependencyNote } from '@/lib/dependencies';
import { heldNote, holdNeedsAPerson, isHeld } from '@/lib/fleet';
import { unmergedDependencies } from '@/lib/merge-order';
import { cn } from '@/lib/utils';

/**
 * The plan, as a list of steps.
 *
 * It used to live in a 400px rail, and the width is what shaped it: the rows
 * were flat rather than indented because a chain of six dependencies pushed the
 * last title off the right edge, and the title was truncated to one line because
 * there was no room for two. Both of those were the rail's constraints, not the
 * plan's. On a page the row can carry the whole title over two lines and still
 * have room for the edge chip, the repo, the size and the state on one line
 * under it.
 *
 * The order is still the topological one the hub sends, so a step appears after
 * everything it depends on, and "after T-1" says what an indent would have.
 *
 * The title is a button rather than a link: it selects the step on this page,
 * the same selection the graph makes, so reading the plan and reading one step
 * of it happen in one place. The step's own page is one link away from there.
 */
export function JobPlan({
  tasks,
  onComment,
  commentable,
  awaiting,
  added,
  mayShape,
  selectedId,
  onSelect,
}: {
  tasks: readonly Task[];
  onComment: (key: string) => void;
  commentable: boolean;
  /** The job is a draft nobody has asked for a plan for yet. */
  awaiting: boolean;
  /** Keys an amendment added that nobody has approved yet; drawn with a marker. */
  added?: ReadonlySet<string>;
  /** Whether this viewer may requeue a held step (the hub enforces it too). */
  mayShape: boolean;
  /** The step the page has open, if any. */
  selectedId?: string | null;
  onSelect: (taskId: string) => void;
}) {
  // Keyed once for every row's dependency check rather than per row: a plan of
  // twenty steps would otherwise rebuild the same lookup twenty times a render.
  const byKey = useMemo(() => {
    const map = new Map<string, Task>();
    for (const t of tasks) map.set(t.key, t);
    return map;
  }, [tasks]);

  if (!tasks.length) {
    return (
      <Empty>
        {awaiting
          ? 'No plan yet. Request one above and a worker drafts it here for your approval.'
          : 'No plan was drafted for this job.'}
      </Empty>
    );
  }
  // Whole-brief, so unlike the capped global list every dependency resolves:
  // a step in review names exactly what has not merged yet (issue #24).
  const statusByKey = new Map(tasks.map((task) => [task.key, task.status]));
  return (
    <ul className="divide-line divide-y">
      {tasks.map((task) => {
        const selected = selectedId === task.id;
        // A queued step with an unmet dependency never carries a hold reason
        // of its own (`dispatchableTasks` never hands it to the scheduler), so
        // the dependency note and the step's own hold are mutually exclusive
        // in practice; this order just says which one wins if that ever stops
        // being true. Stuck gets the verb; every other hold reads as waiting
        // on the thing it is waiting on, with nothing to press
        // (`holdNeedsAPerson`).
        const dep = dependencyNote(task, byKey);
        const stuck = holdNeedsAPerson(task);
        const waiting = isHeld(task) && !stuck;
        const notMergeable =
          task.status === 'review' ? unmergedDependencies(task.dependsOn, statusByKey) : [];
        return (
          <li
            key={task.key}
            className={cn(
              'group flex items-start gap-2.5 px-gutter py-2.5 transition-colors duration-100',
              selected ? 'bg-sel' : 'hover:bg-raised/50',
            )}
          >
            <TaskIcon status={task.status} className="mt-[3px]" />
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-baseline gap-2.5">
                <TaskKey>{task.key}</TaskKey>
                <button
                  type="button"
                  onClick={() => onSelect(task.id)}
                  aria-current={selected ? true : undefined}
                  className="hover:text-gate min-w-0 flex-1 text-left text-sm transition-colors duration-100"
                >
                  {task.title}
                </button>
              </div>
              <div className="text-fg-3 mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-2xs">
                <RepoChip repo={task.repo} />
                <SizeChip size={task.size} />
                {/* A chip, not just a word (issue #64): the repo and size
                    beside it are chips too now, so state reads as one more of
                    the row's shapes rather than the one bare word among them.
                    A dependency stuck one edge upstream (`dep?.blocked`) reads
                    with the same emphasis this step's own held-on-a-person
                    state gets, since neither is going anywhere without a
                    person (see `DependencyNote.blocked` in lib/dependencies.ts);
                    every other wait, this step's own or a dependency's still
                    in flight, stays the quiet secondary tone. */}
                <Badge
                  variant={
                    stuck || dep?.blocked
                      ? 'gate'
                      : waiting || (dep && !dep.blocked)
                        ? 'secondary'
                        : badgeVariantForTone(taskTone(task.status))
                  }
                >
                  {dep?.text ?? (stuck ? 'held' : waiting ? 'waiting' : task.status)}
                </Badge>
                {/* The reason, for the holds that have no verb: without it the
                    row says a step is waiting and not what on, which reads as
                    broken rather than as a device about to come online. A step
                    naming its own unmet dependency (`dep`) already said what
                    it is waiting on in the span above, so this is only for the
                    scheduler's own hold on a step whose dependencies are met. */}
                {!dep && waiting ? (
                  <span className="min-w-0 truncate" title={heldNote(task) ?? undefined}>
                    {heldNote(task)}
                  </span>
                ) : null}
                {/* Unconditional on status: a held or failed step keeps its
                    branch and PR by design (14 days, docs/JOB-LIFECYCLE.md
                    decision 2), so "held" alone would read as the work having
                    gone missing rather than as the state it is actually in
                    (issue #29). */}
                <PrMark prUrl={task.prUrl} prDraft={task.prDraft} branch={task.branch} linked />
                {added?.has(task.key) ? (
                  <span className="bg-gate-bg text-gate shrink-0 rounded-sm px-1 font-medium">
                    added
                  </span>
                ) : null}
                {task.pausedAt ? <PausedChip /> : null}
                {task.dependsOn.length ? (
                  <span className="flex flex-wrap items-center gap-1">
                    <span className="text-fg-3">after</span>
                    <DependencyChips
                      keys={task.dependsOn}
                      resolve={(key) => {
                        const sibling = byKey.get(key);
                        return sibling ? { taskId: sibling.id, title: sibling.title } : undefined;
                      }}
                    />
                  </span>
                ) : null}
                {/* A PR in review can still be the wrong one to merge next: its
                    own dependency's PR may still be open. This is the fact
                    `dependenciesMet` does not carry, because the scheduler is
                    content with a dependency merely in review (issue #24).
                    Quiet, not gate-coloured: `notMergeable` only ever shows on
                    a step already in review, whose state word above is already
                    sitting on `taskTone`'s gate colour for that status. Painting
                    the chip the same colour would put two loud things on one
                    row for a single fact "this needs a person" that the state
                    word already carries; the chip only adds which PR to click
                    first, which is the "after ..." chip's own quiet register. */}
                {notMergeable.length ? (
                  <span
                    className="bg-raised text-fg-2 shrink-0 rounded-sm px-1 font-medium"
                    title={`Not mergeable yet: waits on ${notMergeable.join(', ')}.`}
                  >
                    not mergeable yet
                  </span>
                ) : null}
              </div>
            </div>
            {stuck && mayShape ? <RetryHeldButton taskId={task.id} /> : null}
            {commentable ? (
              <Button
                variant="ghost"
                size="xs"
                className="shrink-0 opacity-0 transition-opacity duration-100 group-hover:opacity-100 focus-visible:opacity-100"
                onClick={() => onComment(task.key)}
              >
                <MessageSquarePlus aria-hidden />
                Note
              </Button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
